type Account = {
    id: string;
    name: string;
    owner: string;
    client: string;
    active: boolean;
    role: Role;
}

type User = {
    id: string;
    username: string;
    firstName: string;
    lastName: string;
    ownerShortName: string;
    ownerName: string;
    roles: Array<Role>;
    lastLoginDate: string;
    active: boolean;
    apiKey: string;
    apiKeyCreationDate: string;
    apiSecret: string;
    apiLabel: string;
}

type Company = {
    id: string;
    name: string;
    shortName: string;
    address1: string;
    address2: string;
    city: string;
    region: string;
    code: string;
    countryCode: string;
    roles: Array<Role>;
    active: boolean;
    endOfDay?: string;
    timeZoneId: string;
    activeUserCount: number;
}

type Role = {
    id: string;
    name: string;
}

type Exchange = {
    id: string;
    name: string;
    mic: string;
    countryCode: string;
    active: boolean;
    ownerName: string;
}

type Product = {
    id: string;
    assetClass: string;
    contractCode: string;
    gmiCode: string;
    currency: string;
    underlier: string;
    multiplier: number;
    contractSize: number;
    uom: string;
    settlementType: string;
    maturityRule: string;
    url: string;
    mic: string;
    shortDescription: string;
    description: string;
    active: boolean;
};

type Notification = {
    id: string;
    client: string;
    clearingAccount: string;
    teamName: string;
    channelName: string;
}

type Trade = {
    id: string;
    tradeID: string;
    traceID?: string;
    tradeDate: string;
    assetClass: string;
    tradeType?: string;
    sourceSystem?: string;
    side?: string;
    quantity: number;
    price: number;
    instrumentCode: string;
    maturity: string | number;
    mic: string;
    client: string;
    clientTrader?: string;
    executingBroker: string;
    executingAccount?: string;
    productDescription?: string;
    optionType?: string;
    strike?: number;
    clearingAccount: string;
    clearingBroker: string;
    contraBroker?: string;
    contraTrader?: string;
    comment?: string;
    executionTime?: string;
    giveupTime?: string;
    carryBroker?: string
    reportedTime: string;
    subAccountCode?: string;
}

type CMEDirectTrade = {
    'Trade Date': string;
    'Created Time': string;
    'Exec Time': string;
    'CC': string;
    'Product': string;
    'Description': string;
    'Price': string;
    'Qty': string;
    'Qty Units': string;
    'Cleared Contracts': string;
    'Notional Volume': string;
    'Type': string;
    'Status': string;
    'Clr Ref': string;
    'Status Msg': string;
    'B Affirm'?: string;
    'S Affirm'?: string;
    'B Bkr Desk': string;
    'S Bkr Desk': string;
    'Buyer': string;
    'B Acct': string;
    'Seller': string;
    'S Acct': string;
    'CH': string;
    'Deal ID': string;
    'Trade ID': string;
    'L/V': string;
    'P&C': string;
    'Entered By': string;
    'Source': string;
}

type ICEBlockTrade = {
    'ICE ID': string;
    'Leg ID': string;
    'Trade Date': string;
    'Product': string;
    'Hub': string;
    'Contract': string;
    'Date Range': string;
    'Price': string;
    'Strike Price': string;
    'Option': string;
    'Premium': string;
    'Quantity': string;
    'Total Quantity': string;
    'Buyer Company': string;
    'Buyer': string;
    'Buyer Clearing Acct': string;
    'Buyer Cust Acct': string;
    'Seller Company': string;
    'Seller': string;
    'Seller Clearing Acct': string;
    'Seller Cust Acct': string;
    'Opposing Broker': string;
    'Submitting Agent ID': string;
    'Transaction Details': string;
    'TT': string;
    'Bust': string;
    'YJGI': string;
    'USI': string;
    'Submission Time': string;
    'Bypass MQR': string;
    'Buyer CDI Override': string;
    'Seller CDI Override': string;
    'Broker Name': string;
}

type WebICETrade = {
    'Trade Date' : string;
    'Trade Time' : string;
    'Deal ID' : string;
    'Leg ID' : string;
    'Orig ID' : string;
    'B/S' : string;
    'Product' : string;
    'Hub' : string;
    'Contract' : string;
    'Clearing Acct' : string;
    'Cust Acct' : string;
    'Clearing Firm' : string;
    'Price' : string;
    'Price Units' : string;
    'Option' : string;
    'Strike' : string;
    'Strike 2' : string;
    'Style' : string;
    'Lots' : string;
    'Total Quantity' : string;
    'Qty Units' : string;
    'TT' : string;
    'Trader' : string;
    'User ID' : string;
    'BRK' : string;
    'Memo' : string;
    'Source' : string;
    'Link ID' : string;
    'USI' : string;
    'Authorized Trader ID' : string;
    'Waiver Ind' : string;
    'Trade Time (micros)' : string;
    'CDI Override' : string;
    'By Pass MQR' : string;
    'Broker Name' : string;
    'Trading Company' : string;
    'MIC' : string;
    'CC' : string;
}

type TradeStatistics = {
    tradeCount: number;
    tradeVolume: number;
    compliantTradeCount: number;
    shortName: string;
    tradeDate: string;
    numberOfDays: number;
}

type Alias = {
    id: string;
    ownerShortName: string;
    type: string;
    fieldName: string;
    alias: string;
    value?: string;
}

type Association = {
    id: string;
    type: string;
    principalShortName: string;
    associateShortName: string;
    associationTypeName: string;
}

type SubAccount = {
    id: string;
    client: string;
    code: string;
    externalCode: string;
    name: string;
    active: boolean;
}

type APIResult = {
    id: string;
    status: 'OK' | 'ERROR';
    field?: string;
    message?: string;
    value?: string;
}

type APIResponse = {
    timestamp: string;
    status: number;
    result : APIResult[] | Trade[] | User[] | Product[] | Exchange[];
    currentPage?: number;
    totalPages?: number;
    pageSize?: number;
    totalElement?: number;
    path: string;
}

export const vocsetColumns = {
    tradeID: {field: 'tradeID', header: 'TradeID'},
    tradeDate: {field: 'tradeDate', header: 'Trade Date'},
    side: {field: 'side', header: 'Side'},
    quantity: {field: 'quantity', header: 'Qty', type: 'number'},
    price: {field: 'price', header: 'Price', type: 'number'},
    instrumentCode: {field: 'instrumentCode', header: 'Instrument', entityName: 'Contract'},
    maturity: {field: 'maturity', header: 'Maturity'},
    mic: {field: 'mic', header: 'Exchange', entityName: 'Exchange'},
    client: {field: 'client', header: 'Client', entityName: 'Client'},
    executingBroker: {field: 'executingBroker', header: 'Broker', entityName: 'Executing Broker'},
    executingAccount: {field: 'executingAccount', header: 'Ex Account', entityName: 'Account'},
    productDescription: {field: 'productDescription', header: 'Description'},
    optionType: {field: 'optionType', header: 'Put/Call'},
    assetClass: {field: 'assetClass', header: 'Asset Class'},
    strike: {field: 'strike', header: 'Strike', type: 'number'},
    clearingAccount: {field: 'clearingAccount', header: 'Clr Account', entityName: 'Account'},
    clearingBroker: {field: 'clearingBroker', header: 'Clearer', entityName: 'Clearing Broker'},
    executionTime: {field: 'executionTime', header: 'Exec Time'},
    giveupTime: {field: 'giveupTime', header: 'Submission Time'},
    carryBroker: {field: 'carryBroker', header: 'Carry Broker', entityName: 'Carry Broker'},
    subAccountCode: {field: 'subAccountCode', header: 'Sub Account Code'},
};

export type { Account, Company, User, Role, Exchange, Product, Trade,
    CMEDirectTrade, ICEBlockTrade, WebICETrade, Alias, Association, APIResult, APIResponse,
    Notification, SubAccount, TradeStatistics}
